import { WEEKDAYS } from '../../catalog/format'
import type { Stretch } from '../../plan/forecast'
import { dayLabel } from '../../plan/wording'

const DAY_START = 6 * 60
const DAY_SPAN = 16 * 60

// WeekStrip is the shape of the week at a glance: seven bars, 06:00 at the
// top and 22:00 at the bottom, with each stretch drawn where it falls, and
// under each its two-letter name and hours ("8h", "Off"), so the week reads
// without a tap. Each day is a button that selects it for editing; without
// onSelect (the locked preview on Free) the days are drawn, not buttons.
export default function WeekStrip({
  week,
  selected = null,
  onSelect,
}: {
  week: Stretch[]
  selected?: number | null
  onSelect?: (day: number) => void
}) {
  return (
    <div className="week-strip">
      {WEEKDAYS.map((name, day) => {
        const stretches = week.filter((s) => s.weekday === day)
        const drawn = (
          <>
            <span className="week-strip__bar">
              {stretches.map((s, i) => {
                const top = Math.max(0, (s.start_minute - DAY_START) / DAY_SPAN) * 100
                const bottom = Math.min(1, (s.end_minute - DAY_START) / DAY_SPAN) * 100
                return (
                  <span
                    key={i}
                    className="week-strip__stretch"
                    style={{ top: `${top}%`, height: `${Math.max(0, bottom - top)}%` }}
                  />
                )
              })}
            </span>
            <span className="week-strip__name" aria-hidden="true">{name.slice(0, 2)}</span>
            <span className="week-strip__hours" aria-hidden="true">{dayHours(stretches)}</span>
          </>
        )
        if (!onSelect) {
          return (
            <div key={name} className="week-strip__day week-strip__day--static" aria-label={dayLabel(name, stretches)}>
              {drawn}
            </div>
          )
        }
        return (
          <button
            key={name}
            type="button"
            className="week-strip__day"
            aria-pressed={selected === day}
            aria-label={dayLabel(name, stretches)}
            onClick={() => onSelect(day)}
          >
            {drawn}
          </button>
        )
      })}
    </div>
  )
}

// dayHours is a day's working hours as "8h" or "7.5h", or "Off".
export function dayHours(stretches: Stretch[]): string {
  const minutes = stretches.reduce((sum, s) => sum + (s.end_minute - s.start_minute), 0)
  if (minutes <= 0) return 'Off'
  const hours = Math.round((minutes / 60) * 10) / 10
  return `${hours}h`
}
